import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarClock, Loader2 } from "lucide-react";

const addDays = (n) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};

export default function RevisitModal({ open, onOpenChange, plan, patientName }) {
  const [date, setDate] = useState(addDays(42));
  const [reason, setReason] = useState("Maintenance review");
  const [types, setTypes] = useState([]);
  const [typeId, setTypeId] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDate(addDays(42));
    setReason("Maintenance review");
    api.get("/cliniko/recall-types").then((r) => {
      const t = r.data.recall_types || [];
      setTypes(t);
      setTypeId(t[0]?.id || "");
    }).catch(() => setTypes([]));
  }, [open]);

  const save = async () => {
    if (!date) { toast.error("Pick a revisit date"); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/plans/${plan.id}/recall`, {
        recall_at: date, reason, recall_type_id: typeId || undefined,
      });
      toast.success(data.cliniko_recall ? "Revisit set in Cliniko ✅" : "Revisit reminder saved");
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not set the revisit date");
    } finally {
      setSaving(false);
    }
  };

  const quick = [
    { label: "4 weeks", days: 28 }, { label: "6 weeks", days: 42 },
    { label: "3 months", days: 90 }, { label: "6 months", days: 182 },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="revisit-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarClock className="h-5 w-5 text-teal-600" /> When should {patientName || "they"} revisit?
          </DialogTitle>
          <DialogDescription>
            Sets the next-review date on the maintenance plan. If the patient is in Cliniko, this creates a live recall.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {quick.map((q) => (
              <button key={q.days} onClick={() => setDate(addDays(q.days))} data-testid={`revisit-quick-${q.days}`}
                className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-all ${date === addDays(q.days) ? "bg-teal-600 text-white" : "bg-white text-slate-600 ring-1 ring-slate-200 hover:ring-teal-300"}`}>
                {q.label}
              </button>
            ))}
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-600">Revisit date</label>
            <Input type="date" value={date} min={addDays(1)} onChange={(e) => setDate(e.target.value)} data-testid="revisit-date" className="mt-1" />
          </div>

          {types.length > 0 && (
            <div>
              <label className="text-xs font-semibold text-slate-600">Cliniko recall type</label>
              <Select value={typeId} onValueChange={setTypeId}>
                <SelectTrigger data-testid="revisit-type" className="mt-1"><SelectValue placeholder="Select a recall type" /></SelectTrigger>
                <SelectContent>
                  {types.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <label className="text-xs font-semibold text-slate-600">Note</label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="revisit-reason" className="mt-1" />
          </div>

          <Button onClick={save} disabled={saving} data-testid="revisit-save" className="w-full bg-teal-600 hover:bg-teal-700">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Set revisit reminder"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
